import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TypographyWrapper, {
  TypographyFormat,
  TypographyWeight,
} from '../TypographyWrapper/TypographyWrapper';
import css from './PrivacyPolicy.module.css';

const PrivacyPolicy = (props) => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);

  // prettier-ignore
  return (
    <div className={classes}>
      <TypographyWrapper variant="body1">Last updated: December 31, 2023</TypographyWrapper>
      <TypographyWrapper variant="body1">
        Thank you for choosing to be part of our community at Treet Co. (
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          “Company”
        </TypographyWrapper>
        ,
        {' '}<TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          “we”
        </TypographyWrapper>
        ,
        {' '}<TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          “us”
        </TypographyWrapper>
        ,
        {' '}<TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          “our”
        </TypographyWrapper>
        ). We are committed to protecting your personal information and your right to privacy.
        If you have any questions or concerns about this privacy notice, or our practices with
        regards to your personal information, please contact us at support@treet.co.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
        When you visit our marketplace websites that link to this Privacy Policy
        (the 
        {' '}<TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          “Website”
        </TypographyWrapper>
        ), and more generally, use any of our services (the
        {' '}<TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          “Services”
        </TypographyWrapper>
        , which include the Website), we appreciate that you are trusting us with your personal
        information. We take your privacy very seriously. In this privacy notice, we seek
        to explain to you in the clearest way possible what information we
        collect, how we use it and what rights you have in relation to it. We hope you take some
        time to read through it carefully, as it is important. If there are any terms in this
        privacy notice that you do not agree with, please discontinue use of our Services
        immediately.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
        This privacy notice applies to all information collected through our Services (which, as
        described above, includes our Website), as well as, any related services, sales, marketing
        or events.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}>
        Please read this privacy notice carefully as it will help you understand what we do with the
        information that we collect.
      </TypographyWrapper>
      <h2>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
          TABLE OF CONTENTS
        </TypographyWrapper>
      </h2>
      <a href="#infocollect"><TypographyWrapper variant="body1">1. WHAT INFORMATION DO WE COLLECT?</TypographyWrapper></a>
      <a href="#infouse"><TypographyWrapper variant="body1">2. HOW DO WE USE YOUR INFORMATION?</TypographyWrapper></a>
      <a href="#infoshare"><TypographyWrapper variant="body1">3. WILL YOUR INFORMATION BE SHARED WITH ANYONE?</TypographyWrapper></a>
      <a href="#whoshare"><TypographyWrapper variant="body1">4. WHO WILL YOUR INFORMATION BE SHARED WITH?</TypographyWrapper></a>
      <a href="#cookies"><TypographyWrapper variant="body1">5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</TypographyWrapper></a>
      <a href="#googlemaps"><TypographyWrapper variant="body1">6. DO WE USE GOOGLE MAPS PLATFORM APIS?</TypographyWrapper></a>
      <a href="#inforetain"><TypographyWrapper variant="body1">7. HOW LONG DO WE KEEP YOUR INFORMATION?</TypographyWrapper></a>
      <a href="#infosafe"><TypographyWrapper variant="body1">8. HOW DO WE KEEP YOUR INFORMATION SAFE?</TypographyWrapper></a>
      <a href="#infominors"><TypographyWrapper variant="body1">9. DO WE COLLECT INFORMATION FROM MINORS?</TypographyWrapper></a>
      <a href="#privacyrights"><TypographyWrapper variant="body1">10. WHAT ARE YOUR PRIVACY RIGHTS?</TypographyWrapper></a>
      <a href="#DNT"><TypographyWrapper variant="body1">11. CONTROLS FOR DO-NOT-TRACK FEATURES</TypographyWrapper></a>
      <a href="#caresidents"><TypographyWrapper variant="body1">12. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?</TypographyWrapper></a>
      <a href="#gdpr"><TypographyWrapper variant="body1">13. DO INDIVIDUALS IN THE EU, EEA, UK, AND SWITZERLAND HAVE SPECIFIC PRIVACY RIGHTS?</TypographyWrapper></a>
      <a href="#policyupdates"><TypographyWrapper variant="body1">14. DO WE MAKE UPDATES TO THIS NOTICE?</TypographyWrapper></a>
      <a href="#contact"><TypographyWrapper variant="body1">15. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</TypographyWrapper></a>
      <TypographyWrapper variant="h2" id="infocollect">
        1. WHAT INFORMATION DO WE COLLECT?
      </TypographyWrapper>
      <TypographyWrapper variant="subtitle1">
        Personal information you disclose to us
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : We collect personal information that you provide to us.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
        We collect personal information that you voluntarily provide to us when you register on the
        Website, express an interest in obtaining information about us or our products and Services,
        when you participate in activities on the Website (such as by posting messages in our online
        forums or entering competitions, contests or giveaways) or otherwise when you contact us.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        The personal information that we collect depends on the context of your interactions with us
        and the Website, the choices you make and the products and features you use. The personal
        information we collect may include the following:
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          Personal Information Provided by You.
        </TypographyWrapper>{' '}
        We collect names; email addresses; mailing addresses;
        usernames; passwords; contact preferences; billing addresses; debit/credit card numbers;
        contact or authentication data; phone numbers; and other similar information.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          Payment Data.
        </TypographyWrapper>{' '}
        We may collect data necessary to process your payment if you make purchases,
        such as your payment instrument number (such as a credit card number), and the security code
        associated with your payment instrument. All payment data is stored by Stripe. You may find
        their privacy notice link(s) here: <a href="https://stripe.com/privacy"><TypographyWrapper variant="body1" format={TypographyFormat.Underlined} typographyOverrides={{ display: 'inline' }}>https://stripe.com/privacy</TypographyWrapper></a>.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        All personal information that you provide to us must be true, complete and accurate, and you
        must notify us of any changes to such personal information.
      </TypographyWrapper>
      <TypographyWrapper variant="subtitle1">
       Information automatically collected
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : Some information — such as your Internet Protocol (IP) address and/or
        browser and device characteristics — is collected automatically when you visit our Website.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We automatically collect certain information when you visit, use or navigate the Website.
        This information does not reveal your specific identity (like your name or contact
        information) but may include device and usage information, such as your IP address, browser
        and device characteristics, operating system, language preferences, referring URLs, device
        name, country, location, information about how and when you use our Website and other
        technical information. This information is primarily needed to maintain the security and
        operation of our Website, and for our internal analytics and reporting purposes.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        Like many businesses, we also collect information through cookies and similar technologies.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        The information we collect includes:
      </TypographyWrapper>
      <ul>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            Log and Usage Data. Log and usage data is service-related, diagnostic, usage and
            performance information our servers automatically collect when you access or use our
            Website and which we record in log files. Depending on how you interact with us, this
            log data may include your IP address, device information, browser type and settings and
            information about your activity in the Website (such as the date/time stamps associated
            with your usage, pages and files viewed, searches and other actions you take such as
            which features you use), device event information (such as system activity, error
            reports (sometimes called ‘crash dumps’) and hardware settings).
          </TypographyWrapper>  
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            Device Data. We collect device data such as information about your computer, phone,
            tablet or other device you use to access the Website. Depending on the device used, this
            device data may include information such as your IP address (or proxy server), device
            and application identification numbers, location, browser type, hardware model Internet
            service provider and/or mobile carrier, operating system and system configuration
            information.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            Location Data. We collect location data such as information about your device’s
            location, which can be either precise or imprecise. How much information we collect
            depends on the type and settings of the device you use to access the Website. For
            example, we may use GPS and other technologies to collect geolocation data that tells us
            your current location (based on your IP address). You can opt out of allowing us to
            collect this information either by refusing access to the information or by disabling
            your Location setting on your device. Note however, if you choose to opt out, you may
            not be able to use certain aspects of the Services.
          </TypographyWrapper>
        </li>
      </ul>
      <TypographyWrapper variant="h2" id="infouse">
        2. HOW DO WE USE YOUR INFORMATION?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : We process your information for purposes based on legitimate business
        interests, the fulfillment of our contract with you, compliance with our legal obligations,
        and/or your consent.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We use personal information collected via our Website for a variety of business purposes
        described below. We process your personal information for these purposes in reliance on our
        legitimate business interests, in order to enter into or perform a contract with you, with
        your consent, and/or for compliance with our legal obligations. We indicate the specific
        processing grounds we rely on next to each purpose listed below.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We use the information we collect or receive:
      </TypographyWrapper>
      <ul>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              To facilitate account creation and logon process.
            </TypographyWrapper>{' '}
            If you choose to link your
            account with us to a third-party account (such as your Google or Facebook account), we
            use the information you allowed us to collect from those third parties to facilitate
            account creation and logon process for the performance of the contract.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              To post testimonials.
            </TypographyWrapper>{' '}
            We post testimonials on our Website that may contain
            personal information. Prior to posting a testimonial, we will obtain your consent to use
            your name and the content of the testimonial. If you wish to update, or delete your
            testimonial, please contact us at support@treet.co and be sure to include your name,
            testimonial location, and contact information.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              Request feedback.
            </TypographyWrapper>{' '}
            We may use your information to request feedback and to contact
            you about your use of our Website.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              To enable user-to-user communications.
            </TypographyWrapper>{' '}
            We may use your information in order to
            enable user-to-user communications with each user’s consent.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              To manage user accounts.
            </TypographyWrapper>{' '}
            We may use your information for the purposes of managing
            our account and keeping it in working order.
          </TypographyWrapper>  
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              To send administrative information to you.
            </TypographyWrapper>{' '}
            We may use your personal information
            to send you product, service and new feature information and/or information about
            changes to our terms, conditions, and policies.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              To protect our Services.
            </TypographyWrapper>{' '}
            We may use your information as part of our efforts to
            keep our Website safe and secure (for example, for fraud monitoring and prevention).
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
            To enforce our terms, conditions and policies for business purposes, to comply with
            legal and regulatory requirements or in connection with our contract.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              To respond to legal requests and prevent harm.
            </TypographyWrapper>{' '}
            If we receive a subpoena or other
            legal request, we may need to inspect the data we hold to determine how to respond.
          </TypographyWrapper>
        </li>
        <li>
            <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              Fulfill and manage your orders.
            </TypographyWrapper>{' '}
            We may use your information to fulfill and manage
            your orders, payments, returns, and exchanges made through the Website.
          </TypographyWrapper>
        </li>

        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              Administer prize draws and competitions.
            </TypographyWrapper>{' '}
            We may use your information to
            administer prize draws and competitions when you elect to participate in our
            competitions.
          </TypographyWrapper>
        </li>

        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              To deliver and facilitate delivery of services to the user.
            </TypographyWrapper>{' '}
            We may use your
            information to provide you with the requested service.
          </TypographyWrapper>
        </li>

        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              To respond to user inquiries/offer support to users.
            </TypographyWrapper>{' '}
            We may use your information
            to respond to your inquiries and solve any potential issues you might have with the use
            of our Services.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              To send you marketing and promotional communications.
            </TypographyWrapper>{' '}
            We and/or our third-party
            marketing partners may use the personal information you send to us for our marketing
            purposes, if this is in accordance with your marketing preferences. For example, when
            expressing an interest in obtaining information about us or our Website, subscribing to
            marketing or otherwise contacting us, we will collect personal information from you. You
            can opt-out of our marketing emails at any time (see the “WHAT ARE YOUR PRIVACY RIGHTS”
            below).
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              Deliver targeted advertising to you.
            </TypographyWrapper>{' '}
            We may use your information to develop and
            display personalized content and advertising (and work with third parties who do so)
            tailored to your interests and/or location and to measure its effectiveness.
          </TypographyWrapper>
        </li>
        <li><TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>Marketing & Communications directly from the Brand hosting their site on Treet.</TypographyWrapper></li>
      </ul>
      <TypographyWrapper variant="h2" id="infoshare">
        3. WILL YOUR INFORMATION BE SHARED WITH ANYONE?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : We only share information with your consent, to comply with laws, to
        provide you with services, to protect your rights, or to fulfill business obligations.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We may process or share your data that we hold based on the following legal basis:
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We may share your personal information in the following situations:
      </TypographyWrapper>
      <ul>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              Our Brand Customers.
            </TypographyWrapper>{' '}
            We may share your information with our brand
            customers that you purchase products from.
          </TypographyWrapper>
        </li>
        <li>
            <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}  typographyOverrides={{ display: 'inline' }}>
              Business Transfers.
            </TypographyWrapper>{' '}
            We may share or transfer your information in connection with,
            or during negotiations of, any merger, sale of company assets, financing, or acquisition
            of all or a portion of our business to another company.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              Vendors, Consultants and Other Third-Party Service Providers.
            </TypographyWrapper>{' '}
            We may share your
            data with third-party vendors, service providers, contractors or agents who perform
            services for us or on our behalf and require access to such information to do that work.
            Examples include: payment processing, data analysis, email delivery, hosting services,
            customer service and marketing efforts. We may allow selected third parties to use
            tracking technology on the Website, which will enable them to collect data on our behalf
            about how you interact with our Website over time. This information may be used to,
            among other things, analyze and track data, determine the popularity of certain content,
            pages or features, and better understand online activity. Unless described in this
            notice, we do not share, sell, rent or trade any of your information with third parties
            for their promotional purposes.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              Affiliates.
            </TypographyWrapper>{' '}
            We may share your information with our affiliates, in which case we
            will require those affiliates to honor this privacy notice. Affiliates include our
            parent company and any subsidiaries, joint venture partners or other companies that we
            control or that are under common control with us.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              Business Partners.
            </TypographyWrapper>{' '}
            We may share your information with our business partners to
            offer you certain products, services or promotions.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
            <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
              Other Users.
            </TypographyWrapper>{' '}
            When you share personal information (for example, by posting
            comments, contributions or other content to the Website) or otherwise interact with
            public areas of the Website, such personal information may be viewed by all users and
            may be publicly made available outside the Website in perpetuity. Similarly, other users
            will be able to view descriptions of your activity, communicate with you within our
            Website, and view your profile.
          </TypographyWrapper>
        </li>
      </ul>
      <TypographyWrapper variant="h2" id="whoshare">
        4. WHO WILL YOUR INFORMATION BE SHARED WITH?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : We only share information with the following third parties.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We only share and disclose your information with the following third parties. We have
        categorized each party so that you may be easily understand the purpose of our data
        collection and processing practices. If we have processed your data based on your consent
        and you wish to revoke your consent, please contact us using the contact details provided in
        the section below titled “HOW CAN YOU CONTACT US ABOUT THIS NOTICE?”.
      </TypographyWrapper>
      <ul>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
            Advertising, Direct Marketing, and Lead Generation
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>Google AdSense</TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}  typographyOverrides={{ display: 'inline' }}>
            Allow Users to Connect to Their Third-Party Accounts
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>Facebook account, Stripe account and Instagram account</TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}  typographyOverrides={{ display: 'inline' }}>
            Cloud Computing Services
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>Amazon Web Services (AWS)</TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}  typographyOverrides={{ display: 'inline' }}>
            Communicate and Chat with Users
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>HubSpot Email and Sendgrid and Zendesk Chat and Intercom</TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}  typographyOverrides={{ display: 'inline' }}>
            Content Optimization
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>Google Fonts and Google Site Search</TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}  typographyOverrides={{ display: 'inline' }}>
            Data Backup and Security
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>Amazon Glacier</TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
            Functionality and Infrastructure Optimization
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>Google Cloud Storage and Amazon Web Services</TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}  typographyOverrides={{ display: 'inline' }}>
            Invoice and Billing
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>Stripe</TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
            Retargeting Platforms
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
          Facebook Custom Audience, Facebook Remarketing, Google Ads Remarketing and Google
          Analytics Remarketing
        </TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
            Social Media Sharing and Advertising
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
          Facebook advertising and Instagram advertising
        </TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
            User Account Registration and Authentication
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
          Firebase Authentication and Stripe OAuth
        </TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
            Web and Mobile Analytics
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
          Facebook Analytics, Facebook Ads conversion tracking, Google Analytics, Google Ads and
          HubSpot Analytics
        </TypographyWrapper>
        <li>
          <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
            Website Hosting
          </TypographyWrapper>
        </li>
        <TypographyWrapper variant="body1" typographyOverrides={{ display: 'inline' }}>
          Sharetribe
        </TypographyWrapper>
      </ul>
      <TypographyWrapper variant="h2" id="cookies">
        5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : We may use cookies and other tracking technologies to collect and store
        your information.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We may use cookies and similar tracking technologies (like web beacons and pixels) to access
        or store information. Specific information about how we use such technologies and how you
        can refuse certain cookies is set out in our Cookie Notice.
      </TypographyWrapper>
      <TypographyWrapper variant="h2" id="googlemaps">
        6. DO WE USE GOOGLE MAPS PLATFORM APIS?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : Yes, we use Google Maps Platform APIs for the purpose of providing better
        service.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        This Website uses Google Maps Platform APIs which are subject to Google’s Terms of Service.
        You may find the Google Maps Platform Terms of Service <a href="https://cloud.google.com/maps-platform/terms/"><TypographyWrapper variant="body1" format={TypographyFormat.Underlined} typographyOverrides={{ display: 'inline' }}>here</TypographyWrapper></a>. To find out more about Google’s
        Privacy Policy, please refer to this <a href="https://policies.google.com/privacy"><TypographyWrapper variant="body1" format={TypographyFormat.Underlined} typographyOverrides={{ display: 'inline' }}>link</TypographyWrapper></a>. We obtain and store on your device (‘cache’) your
        location. You may revoke your consent anytime by contacting us at the contact details
        provided at the end of this document.
      </TypographyWrapper>
      <TypographyWrapper variant="h2" id="inforetain">
        7. HOW LONG DO WE KEEP YOUR INFORMATION?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : We keep your information for as long as necessary to fulfill the purposes
        outlined in this privacy notice unless otherwise required by law.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We will only keep your personal information for as long as it is necessary for the purposes
        set out in this privacy notice, unless a longer retention period is required or permitted by
        law (such as tax, accounting or other legal requirements). No purpose in this notice will
        require us keeping your personal information for longer than twelve (12) months past the
        termination of the user’s account.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        When we have no ongoing legitimate business need to process your personal information, we
        will either delete or anonymize such information, or, if this is not possible (for example,
        because your personal information has been stored in backup archives), then we will securely
        store your personal information and isolate it from any further processing until deletion is
        possible.
      </TypographyWrapper>
      <TypographyWrapper variant="h2" id="infosafe">
        8. HOW DO WE KEEP YOUR INFORMATION SAFE?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : We aim to protect your personal information through a system of
        organizational and technical security measures.
      </TypographyWrapper>  
      <TypographyWrapper variant="body1">
        We have implemented appropriate technical and organizational security measures designed to
        protect the security of any personal information we process. However, despite our safeguards
        and efforts to secure your information, no electronic transmission over the Internet or
        information storage technology can be guaranteed to be 100% secure, so we cannot promise or
        guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able
        to defeat our security, and improperly collect, access, steal, or modify your information.
        Although we will do our best to protect your personal information, transmission of personal
        information to and from our Website is at your own risk. You should only access the Website
        within a secure environment.
      </TypographyWrapper>
      <TypographyWrapper variant="h2" id="infominors">
        9. DO WE COLLECT INFORMATION FROM MINORS?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : We do not knowingly collect data from or market to children under 18 years
        of age.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We do not knowingly solicit data from or market to children under 18 years of age. By using
        the Website, you represent that you are at least 18 or that you are the parent or guardian
        of such a minor and consent to such minor dependent’s use of the Website. If we learn that
        personal information from users less than 18 years of age has been collected, we will
        deactivate the account and take reasonable measures to promptly delete such data from our
        records. If you become aware of any data we may have collected from children under age 18,
        please contact us at support@treet.co.
      </TypographyWrapper>
      <TypographyWrapper variant="h2" id="privacyrights">
        10. WHAT ARE YOUR PRIVACY RIGHTS?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : You may review, change, or terminate your account at any time.
      </TypographyWrapper>
      <TypographyWrapper variant="subtitle1" weight={TypographyWeight.Bold}>
        Account Information
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        If you would at any time like to review or change the information in your account or
        terminate your account, you can:
      </TypographyWrapper>
      <ul>
        <li>
          <TypographyWrapper variant="body1">Contact us using the contact information provided.</TypographyWrapper>
        </li>
      </ul>
      <TypographyWrapper variant="body1">
        Upon your request to terminate your account, we will deactivate or delete your account and
        information from our active databases. However, we may retain some information in our files
        to prevent fraud, troubleshoot problems, assist with any investigations, enforce our Terms
        of Use and/or comply with applicable legal requirements.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          Cookies and similar technologies:
        </TypographyWrapper>{' '}
        Most Web browsers are set to accept cookies by default. If you prefer, you can usually
        choose to set your browser to remove cookies and to reject cookies. If you choose to remove
        cookies or reject cookies, this could affect certain features or services of our Website. To
        opt-out of interest-based advertising by advertisers on our Website visit{' '}
        <a href="http://www.aboutads.info/choices/"><TypographyWrapper variant="body1" format={TypographyFormat.Underlined} typographyOverrides={{ display: 'inline' }}>http://www.aboutads.info/choices/</TypographyWrapper></a>.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          Opting out of email marketing:
        </TypographyWrapper>{' '}
        You can unsubscribe from our marketing email list at any time by clicking on the unsubscribe
        link in the emails that we send or by contacting us using the details provided below. You
        will then be removed from the marketing email list — however, we may still communicate with
        you, for example to send you service-related emails that are necessary for the
        administration and use of your account, to respond to service requests, or for other
        non-marketing purposes. To otherwise opt-out, you may:
      </TypographyWrapper>
      <ul>
        <li>
          <TypographyWrapper variant="body1">Contact us using the contact information provided.</TypographyWrapper>
        </li>
      </ul>
      <TypographyWrapper variant="h2" id="DNT">
        11. CONTROLS FOR DO-NOT-TRACK FEATURES
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        Most web browsers and some mobile operating systems and mobile applications include a
        Do-Not-Track (“DNT”) feature or setting you can activate to signal your privacy preference
        not to have data about your online browsing activities monitored and collected. At this
        stage no uniform technology standard for recognizing and implementing DNT signals has been
        finalized. As such, we do not currently respond to DNT browser signals or any other
        mechanism that automatically communicates your choice not to be tracked online. If a
        standard for online tracking is adopted that we must follow in the future, we will inform
        you about that practice in a revised version of this privacy notice.
      </TypographyWrapper>
      <TypographyWrapper variant="h2" id="caresidents">
        12. DO CALIFORNIA RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : Yes, if you are a resident of California, you are granted specific rights
        regarding access to your personal information.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        California Civil Code Section 1798.83, also known as the “Shine The Light” law, permits our
        users who are California residents to request and obtain from us, once a year and free of
        charge, information about categories of personal information (if any) we disclosed to third
        parties for direct marketing purposes and the names and addresses of all third parties with
        which we shared personal information in the immediately preceding calendar year. If you are
        a California resident and would like to make such a request, please submit your request in
        writing to us using the contact information provided below.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        If you are under 18 years of age, reside in California, and have a registered account with
        the Website, you have the right to request removal of unwanted data that you publicly post
        on the Website. To request removal of such data, please contact us using the contact
        information provided below, and include the email address associated with your account and a
        statement that you reside in California. We will make sure the data is not publicly
        displayed on the Website, but please be aware that the data may not be completely or
        comprehensively removed from all our systems (e.g. backups, etc.).
      </TypographyWrapper>
      <TypographyWrapper variant="h2" id="ccpa">
        CCPA Privacy Notice
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        The California Code of Regulations defines a “resident” as:
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        (1) every individual who is in the State of California for other than a temporary or
        transitory purpose and
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        (2) every individual who is domiciled in the State of California who is outside the State of
        California for a temporary or transitory purpose
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        All other individuals are defined as “non-residents.”
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        If this definition of “resident” applies to you, we must adhere to certain rights and
        obligations regarding your personal information.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        What categories of personal information do we collect?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We have collected the following categories of personal information in the past twelve (12)
        months:
      </TypographyWrapper>
      <table style={{ width: '100%' }} border="1">
        <tbody>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}>
                &nbsp;Category
              </TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}>
                &nbsp;Examples
              </TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}>
                &nbsp;Collected
              </TypographyWrapper>
            </td>
          </tr>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;A. Identifiers</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">
                &nbsp;Contact details, such as real name, alias, postal address, telephone or mobile
                contact number, unique personal identifier, online identifier, Internet Protocol
                address, email address and account name
              </TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;NO</TypographyWrapper>
            </td>
          </tr>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">
                &nbsp;B. Personal information categories listed in the California Customer Records
                statute
              </TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">
                &nbsp;Name, contact information, education, employment, employment history and
                financial information
              </TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">YES</TypographyWrapper>
            </td>
          </tr>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">
                &nbsp;C. Protected classification characteristics under California or federal law
              </TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;Gender and date of birth</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">NO</TypographyWrapper>
            </td>
          </tr>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;D. Commercial information</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">
                &nbsp;Transaction information, purchase history, financial details and payment
                information
              </TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">NO</TypographyWrapper>
            </td>
          </tr>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;E. Biometric information</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
                <TypographyWrapper variant="body1">&nbsp;Fingerprints and voiceprints</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">NO</TypographyWrapper>
            </td>
          </tr>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;F. Internet or other similar network activity</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">
                &nbsp;Browsing history, search history, online behavior, interest data, and
                interactions with our and other websites, applications, systems and advertisements
              </TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">NO</TypographyWrapper>
            </td>
          </tr>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;G. Geolocation data</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;Device location</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;NO</TypographyWrapper>
            </td>
          </tr>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;H. Audio, electronic, visual, thermal, olfactory, or similar information</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">
                &nbsp;Images and audio, video or call recordings created in connection with our
                business activities
              </TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;NO</TypographyWrapper>
            </td>
          </tr>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;I. Professional or employment-related information</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">
                &nbsp;Business contact details in order to provide you our services at a business
                level, job title as well as work history and professional qualifications if you
                apply for a job with us
              </TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;NO</TypographyWrapper>
            </td>
          </tr>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;J. Education Information</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;Student records and directory information</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;</TypographyWrapper>
            </td>
          </tr>
          <tr style={{ height: '21px' }}>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;K. Inferences drawn from other personal information</TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">
                &nbsp;Inferences drawn from any of the collected personal information listed above
                to create a profile or summary about, for example, an individual&rsquo;s preferences
                and characteristics
              </TypographyWrapper>
            </td>
            <td style={{ height: '21px' }}>
              <TypographyWrapper variant="body1">&nbsp;YES</TypographyWrapper>
            </td>
          </tr>
        </tbody>
      </table>
      <TypographyWrapper variant="body1">
        We may also collect other personal information outside of these categories instances where
        you interact with us in-person, online, or by phone or mail in the context of:
      </TypographyWrapper>
      <ul>
        <li>
          <TypographyWrapper variant="body1">Receiving help through our customer support channels;</TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1">Participation in customer surveys or contests; and</TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1">Facilitation in the delivery of our Services and to respond to your inquiries.</TypographyWrapper>
        </li>
      </ul>
      <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}>
        How do we use and share your personal information?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        Treet Co. collects and shares your personal information through:
      </TypographyWrapper>
      <ul>
        <li>
          <TypographyWrapper variant="body1">Targeting cookies/Marketing cookies</TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1">Social media cookies</TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1">Beacons/Pixels/Tags</TypographyWrapper>
        </li>
      </ul>
      <TypographyWrapper variant="body1">
        More information about our data collection and sharing practices can be found in this
        privacy notice.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        You may contact us by email at support@treet.co, or by referring to the contact details at
        the bottom of this document.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        If you are using an authorized agent to exercise your right to opt-out we may deny a request
        if the authorized agent does not submit proof that they have been validly authorized to act
        on your behalf.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}>
        Will your information be shared with anyone else?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We may disclose your personal information with our service providers pursuant to a written
        contract between us and each service provider. Each service provider is a for-profit entity
        that processes the information on our behalf.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        The current list of our service providers can be found in below.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We may use your personal information for our own business purposes, such as for undertaking
        internal research for technological development and demonstration. This is not considered to
        be “selling” of your personal data.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        Treet Co. has disclosed the following categories of personal information to third parties
        for a business or commercial purpose in the preceding twelve (12) months:
      </TypographyWrapper>
      <ul>
        <li>
          <TypographyWrapper variant="body1">
            Category B. Personal information, as defined in the California Customer Records law,
            such as your name, contact information, education, employment, employment history and
            financial information.
          </TypographyWrapper>
        </li>
      </ul>
      <TypographyWrapper variant="body1">
        The categories of third parties to whom we disclosed personal information for a business or
        commercial purpose can be found under “WHO WILL YOUR INFORMATION BE SHARED WITH?”.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}>
        Your rights with respect to your personal data
      </TypographyWrapper>
      <TypographyWrapper variant="body1" format={TypographyFormat.Underlined}>
        Right to request deletion of the data - Request to delete
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        You can ask for the deletion of your personal information. If you ask us to delete your
        personal information, we will respect your request and delete your personal information,
        subject to certain exceptions provided by law, such as (but not limited to) the exercise by
        another consumer of his or her right to free speech, our compliance requirements resulting
        from a legal obligation or any processing that may be required to protect against illegal
        activities.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" format={TypographyFormat.Underlined}>
        Right to be informed - Request to know
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        Depending on the circumstances, you have a right to know:
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        whether we collect and use your personal information;
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        the categories of personal information that we collect;
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        the purposes for which the collected personal information is used;
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        whether we sell your personal information to third parties;
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        the categories of personal information that we sold or disclosed for a business purpose;
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        the categories of third parties to whom the personal information was sold or disclosed for a
        business purpose; and
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        the business or commercial purpose for collecting or selling personal information.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        In accordance with applicable law, we are not obligated to provide or delete consumer
        information that is de-identified in response to a consumer request or to re-identify
        individual data to verify a consumer request.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" format={TypographyFormat.Underlined}>
        Right to Non-Discrimination for the Exercise of a Consumer’s Privacy Rights
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We will not discriminate against you if you exercise your privacy rights.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" format={TypographyFormat.Underlined}>
        Verification process
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        Upon receiving your request, we will need to verify your identity to determine you are the
        same person about whom we have the information in our system. These verification efforts
        require us to ask you to provide information so that we can match it with information you
        have previously provided us. For instance, depending on the type of request you submit, we
        may ask you to provide certain information so that we can match the information you provide
        with the information we already have on file, or we may contact you through a communication
        method (e.g. phone or email) that you have previously provided to us. We may also use other
        verification methods as the circumstances dictate.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We will only use personal information provided in your request to verify your identity or
        authority to make the request. To the extent possible, we will avoid requesting additional
        information from you for the purposes of verification. If, however, we cannot verify your
        identity from the information already maintained by us, we may request that you provide
        additional information for the purposes of verifying your identity, and for security or
        fraud-prevention purposes. We will delete such additionally provided information as soon as
        we finish verifying you.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" format={TypographyFormat.Underlined}>
        Other privacy rights
      </TypographyWrapper>
      <ul>
        <li>
          <TypographyWrapper variant="body1">you may object to the processing of your personal data</TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1">
            you may request correction of your personal data if it is incorrect or no longer
            relevant, or ask to restrict the processing of the data
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1">
            you can designate an authorized agent to make a request under the CCPA on your behalf.
            We may deny a request from an authorized agent that does not submit proof that they have
            been validly authorized to act on your behalf in accordance with the CCPA.
          </TypographyWrapper>
        </li>
        <li>
          <TypographyWrapper variant="body1">
            you may request to opt-out from future selling of your personal information to third
            parties. Upon receiving a request to opt-out, we will act upon the request as soon as
            feasibly possible, but no later than 15 days from the date of the request submission.
            You may also initiate any opt outs by broadcasting an Opt-Out Preference Signal, such
            as the Global Privacy Control (GPC). We honor Opt-Out Preference Signals, including GPC.
            If you choose to use an Opt-Out Preference Signal, you will need to turn it on for each
            supported browser or browser extension you use.
          </TypographyWrapper>
        </li>
      </ul>
      <TypographyWrapper variant="body1">
        To exercise these rights, you can contact us by email at support@treet.co, or by
        referring to the contact details at the bottom of this document. If you have a complaint
        about how we handle your data, we would like to hear from you.
      </TypographyWrapper>
      <TypographyWrapper variant="h2" id="gdpr">
        13. DO INDIVIDUALS IN THE EU, EEA, UK, AND SWITZERLAND HAVE SPECIFIC
        PRIVACY RIGHTS?
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : Yes, if you are located in the European Union, European Economic Area,
        United Kingdom, or Switzerland, you are granted specific rights regarding your
        Personal Data. This section provides additional information regarding Treet’s processing
        of personal data of people located in the European Union (“EU”), European Economic
        Area (“EEA”), Switzerland, and the United Kingdom (“UK”) in accordance with the
        EU Data Protection Regulation, UK Data Protection Regulation, and the Swiss Federal Data
        Protection Act. For purposes of this section, “Personal Data” has the definition set
        forth in the foregoing data protection laws.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" format={TypographyFormat.Underlined} weight={TypographyWeight.Bold}>
        Legal Basis for Processing
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        Our legal basis for processing Personal Data depends on the Personal Data concerned
        and the context in which we process it. We process Personal Data from you where we
        need it to perform a contract with you, where the processing is in our legitimate
        interests (including the purposes described in this privacy notice), where the processing
        is necessary for us to meet our applicable legal obligations, or if we otherwise
        have your consent.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" format={TypographyFormat.Underlined} weight={TypographyWeight.Bold}>
        Special Category Data
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        Treet does not intend to collect any Special Category Data, which is any data that reveals
        your racial or ethnic origin, political opinions, religious, moral or philosophical
        beliefs, trade union membership, political views, the processing of genetic data, biometric
        data for the purpose of identifying a person, and data concerning health or a person’s
        sex life and/or sexual orientation.  Please refrain from sending us any Special Category
        Data.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" format={TypographyFormat.Underlined} weight={TypographyWeight.Bold}>
        Automated Decision Making
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        Treet does not make any automated decisions on your behalf or about you without first
        obtaining your express, opt-in consent. In the event we secure your consent to do so,
        you have the right to object to the processing of Personal Data via automated decision
        making at any time by contacting us at support@treet.co.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" format={TypographyFormat.Underlined} weight={TypographyWeight.Bold}>
        Transfers to Third Parties and Countries
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        Personal Data that we collect or receive may be transferred to and/or processed by third
        parties that are located outside of the EU, EEA, Switzerland or the UK, some of which
        applicable authorities may not consider to have an adequate level of protection
        for Personal Data. Treet will only transfer Personal Data to third parties located outside
        of the EU, EEA, Switzerland, and the UK when it has ensured appropriate safeguards for
        such Personal Data through use of the standard contractual clauses or other lawful
        and approved methods.
      </TypographyWrapper>
      <TypographyWrapper variant="body1" weight={TypographyWeight.Bold}>
        Your Privacy Rights
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        You have the right to request access to your Personal Data, to have your Personal
        Data corrected, restricted or deleted, to withdraw any consent that you have given to
        the processing of your Personal Data (without affecting the lawfulness of the processing
        prior to your withdrawal of consent) and to object to our processing of your Personal
        Data.  You also have the right of data portability in certain circumstances, which means
        that you can request that we provide you with a transferable copy of personal data that
        you have provided to us.  Please note that your rights may be subject to various
        exceptions. If you wish to exercise any of these rights, or if you have any concerns
        about our processing of your Personal Data, please contact us at support@treet.co.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        You also have the right to lodge a complaint with a supervisory authority or other
        regulatory agency if you believe we have violated any of the rights afforded to you
        under applicable data protection laws. We encourage you to reach out to us
        first so we have an opportunity to address your concerns directly before you do so.
      </TypographyWrapper>
      <TypographyWrapper variant="h2" id="policyupdates">14. DO WE MAKE UPDATES TO THIS NOTICE?</TypographyWrapper>
      <TypographyWrapper variant="body1">
        <TypographyWrapper variant="body1" weight={TypographyWeight.Bold} typographyOverrides={{ display: 'inline' }}>
          In Short
        </TypographyWrapper>
        : Yes, we will update this notice as necessary to stay compliant with
        relevant laws.
      </TypographyWrapper>
      <TypographyWrapper variant="body1">
        We may update this privacy notice from time to time. The updated version will be indicated
        by an updated “Revised” date and the updated version will be effective as soon as it is
        accessible. If we make material changes to this privacy notice, we may notify you either by
        prominently posting a notice of such changes or by directly sending you a notification. We
        encourage you to review this privacy notice frequently to be informed of how we are
        protecting your information.
      </TypographyWrapper>
      <TypographyWrapper variant="h2" id="contact">15. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</TypographyWrapper>
      <TypographyWrapper variant="body1">
        If you have questions or comments about this notice, you may email us at support@treet.co.
      </TypographyWrapper>
    </div>
  );
};

PrivacyPolicy.defaultProps = {
  rootClassName: null,
  className: null,
};

const { string } = PropTypes;

PrivacyPolicy.propTypes = {
  rootClassName: string,
  className: string,
};

export default PrivacyPolicy;
